<script lang="ts">
  import { phasesModalOpen, closePhasesModal, currentProjectId, currentProject, projectPhases, updateProjectPhases } from "../lib/stores";
  import { TAG_COLORS } from "../lib/subcontractors";
  import type { PhaseDef } from "../lib/phases";

  let rows: PhaseDef[] = [];
  let saving = false;
  let error = "";

  // Populated fresh each time the modal opens for a given project, so switching
  // projects between opens (or someone else saving meanwhile) doesn't leave a stale
  // edit sitting around.
  let lastOpenedFor: string | null = null;
  $: if ($phasesModalOpen && $currentProjectId && $currentProjectId !== lastOpenedFor) {
    lastOpenedFor = $currentProjectId;
    rows = $projectPhases.map((p) => ({ ...p }));
    error = "";
  }
  $: if (!$phasesModalOpen) lastOpenedFor = null;

  function nextColor(): string {
    const used = new Set(rows.map((r) => r.color));
    return TAG_COLORS.find((c) => !used.has(c.hex))?.hex ?? TAG_COLORS[0].hex;
  }

  function addRow() {
    rows = [...rows, { name: "New lane", color: nextColor() }];
  }
  function removeRow(i: number) {
    rows = rows.filter((_, idx) => idx !== i);
  }
  function moveRow(i: number, delta: number) {
    const j = i + delta;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    rows = next;
  }
  function pickColor(i: number, hex: string, e: Event) {
    rows = rows.map((r, idx) => (idx === i ? { ...r, color: hex } : r));
    (e.currentTarget as HTMLElement).closest("details")?.removeAttribute("open");
  }

  async function save() {
    if (!$currentProjectId) return;
    const trimmed = rows.map((r) => ({ ...r, name: r.name.trim() })).filter((r) => r.name);
    if (!trimmed.length) { error = "Add at least one swimlane."; return; }
    saving = true;
    error = "";
    try {
      await updateProjectPhases($currentProjectId, trimmed);
      closePhasesModal();
    } catch (e) {
      error = e instanceof Error ? e.message : "Couldn't save swimlanes.";
    } finally {
      saving = false;
    }
  }

  function onOverlayClick(e: MouseEvent) {
    if (e.target === e.currentTarget) closePhasesModal();
  }
  function onKeydown(e: KeyboardEvent) {
    if (e.key === "Escape") closePhasesModal();
  }
</script>

<svelte:window on:keydown={onKeydown} />

{#if $phasesModalOpen && $currentProject}
  <div class="modal-overlay" on:click={onOverlayClick} role="presentation">
    <div class="modal" role="dialog" aria-modal="true" aria-labelledby="phasesModalTitle">
      <div class="modal-head">
        <div class="modal-title" id="phasesModalTitle">Swimlanes — {$currentProject.name}</div>
        <button class="modal-close" type="button" aria-label="Close" on:click={closePhasesModal}>✕</button>
      </div>
      <div class="modal-body">
        <div class="phase-rows">
          {#each rows as row, i (i)}
            <div class="phase-row">
              <details class="subc-color-item">
                <summary>
                  <span class="subc-swatch" style="background:{row.color}"></span>
                </summary>
                <div class="subc-color-grid" role="group" aria-label="Pick a color for {row.name || 'this lane'}">
                  {#each TAG_COLORS as c (c.hex)}
                    <button type="button" class="subc-swatch-btn" style="background:{c.hex}" title={c.name}
                      aria-label={c.name}
                      aria-pressed={row.color === c.hex}
                      on:click={(e) => pickColor(i, c.hex, e)}
                    ></button>
                  {/each}
                </div>
              </details>
              <input type="text" bind:value={row.name} placeholder="Lane name" />
              <div class="phase-row-actions">
                <button class="btn" type="button" title="Move up" disabled={i === 0} on:click={() => moveRow(i, -1)}>▲</button>
                <button class="btn" type="button" title="Move down" disabled={i === rows.length - 1} on:click={() => moveRow(i, 1)}>▼</button>
                <button class="btn" type="button" title="Remove this lane" on:click={() => removeRow(i)}>✕</button>
              </div>
            </div>
          {/each}
        </div>
        <button class="btn" type="button" on:click={addRow}>+ Add lane</button>

        <div class="modal-hint">
          Renaming or removing a lane doesn't touch existing activities — one left
          pointing at an old name just gets its own lane on the board until someone
          reassigns it in the edit modal.
        </div>

        {#if error}<div class="modal-hint" style="color:var(--status-critical);">{error}</div>{/if}
      </div>
      <div class="modal-foot">
        <div class="spacer"></div>
        <button class="btn" type="button" on:click={closePhasesModal}>Cancel</button>
        <button class="btn primary" type="button" disabled={saving} on:click={save}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .phase-rows { display:flex; flex-direction:column; gap:8px; margin-bottom:10px; }
  .phase-row { display:flex; align-items:center; gap:8px; }
  .phase-row input[type="text"] { flex:1 1 auto; min-width:0; }
  .phase-row-actions { display:flex; gap:4px; flex:none; }
</style>
